/**
 * Animation Variants Adapter
 * 
 * This adapter provides web-specific Framer Motion animation variants that are
 * based on the shared design system animation tokens.
 * 
 * Following the adapter pattern, this centralizes all animation variants for web
 * components while adapting the design system tokens for the specific UI library.
 */

import { Variants } from "framer-motion";
import { 
  animationTimings, 
  animationDelays, 
  staggerIntervals, 
  easings, 
  animationPresets
} from '@repo/design-system/tokens';

/**
 * Container animation variants for staggered children animations
 */
export const containerVariants: Variants = {
  hidden: animationPresets.staggerContainer.initial,
  visible: {
    ...animationPresets.staggerContainer.animate,
    transition: {
      staggerChildren: staggerIntervals.default,
      delayChildren: animationDelays.short,
      when: "beforeChildren"
    }
  }
};

/**
 * Standard item animation for most content elements
 */
export const itemVariants: Variants = {
  hidden: animationPresets.slideInUp.initial,
  visible: (custom = 0) => ({
    ...animationPresets.slideInUp.animate,
    transition: {
      ...animationPresets.springs.gentle,
      delay: animationDelays.short + (custom * staggerIntervals.tight),
    }
  })
};

/**
 * Animation variants specifically for buttons
 */
export const buttonVariants: Variants = {
  hidden: { 
    ...animationPresets.scaleIn.initial,
    y: 20,
  },
  visible: {
    ...animationPresets.scaleIn.animate,
    y: 0,
    transition: {
      ...animationPresets.springs.responsive,
      delay: animationDelays.long
    }
  }
};

/**
 * Container variants for stats sections
 */
export const statsVariants: Variants = {
  hidden: animationPresets.staggerContainer.initial,
  visible: {
    ...animationPresets.staggerContainer.animate,
    transition: {
      staggerChildren: staggerIntervals.loose,
      delayChildren: animationDelays.long,
      when: "beforeChildren"
    }
  }
};

/**
 * Individual stat item variants
 */
export const statItemVariants: Variants = {
  hidden: { 
    ...animationPresets.slideInUp.initial,
    y: 30 
  },
  visible: {
    ...animationPresets.slideInUp.animate,
    transition: animationPresets.springs.gentle
  }
};

/**
 * Fade-in animation for images and backgrounds
 */
export const fadeInVariants: Variants = {
  hidden: animationPresets.fadeIn.initial,
  visible: {
    ...animationPresets.fadeIn.animate,
    transition: {
      duration: animationTimings.extraSlow,
      ease: easings.premium.easeOutQuint
    }
  }
};

/**
 * Scale-up animation for images and cards on hover
 */
export const scaleUpVariants: Variants = {
  initial: { scale: 1 },
  hover: {
    ...animationPresets.hover.scale,
    transition: animationPresets.springs.bouncy
  }
};

/**
 * Staggered list item animations
 */
export const listItemVariants: Variants = {
  hidden: animationPresets.slideInLeft.initial,
  visible: (custom = 0) => ({
    ...animationPresets.slideInLeft.animate,
    transition: {
      ...animationPresets.springs.gentle,
      delay: custom * staggerIntervals.default
    }
  })
};

/**
 * Industry card animation variants with custom hover effects
 */
export const industryCardVariants: Variants = {
  hidden: { 
    opacity: 0, 
    y: 20,
    willChange: 'opacity, transform, box-shadow'
  },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: animationTimings.medium,
      delay: i * staggerIntervals.tight,
      ease: easings.premium.easeOutQuint
    }
  }),
  hover: {
    y: -8,
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
    transition: {
      duration: animationTimings.fast,
      ease: easings.premium.easeOutQuint
    }
  },
  focus: {
    y: -8,
    boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.5)',
    transition: {
      duration: animationTimings.fast,
      ease: easings.premium.easeOutQuint
    }
  }
};

/**
 * Industry grid container variants
 */
export const industryGridVariants: Variants = {
  ...containerVariants,
  hidden: { 
    opacity: 0,
    transition: {
      when: 'afterChildren'
    }
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: staggerIntervals.tight,
      delayChildren: animationDelays.short,
      when: "beforeChildren",
      duration: animationTimings.medium,
      ease: easings.premium.easeOutQuint
    }
  }
};

/**
 * Icon animation variants for industry cards
 */
export const industryIconVariants: Variants = {
  hidden: { 
    scale: 0.8, 
    opacity: 0,
    willChange: 'transform, opacity'
  },
  visible: { 
    scale: 1, 
    opacity: 1,
    transition: {
      duration: animationTimings.medium,
      delay: animationDelays.short,
      ease: easings.premium.easeOutBack
    }
  }
};

/**
 * Category button animation variants
 */
export const categoryButtonVariants: Variants = {
  initial: { 
    scale: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    willChange: 'transform, background-color'
  },
  hover: {
    scale: 1.05,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    transition: {
      duration: animationTimings.fast,
      ease: easings.premium.easeOutBack
    }
  },
  active: {
    scale: 1,
    backgroundColor: 'rgba(79, 70, 229, 1)',
    transition: {
      duration: animationTimings.fast,
      ease: easings.easeOut
    }
  },
  tap: {
    scale: 0.95,
    transition: {
      duration: animationTimings.fast,
      ease: easings.easeOut
    }
  }
};

/**
 * Feature list item animation variants
 */
export const featureItemVariants: Variants = {
  hidden: { 
    opacity: 0, 
    x: -10,
    willChange: 'transform, opacity'
  },
  visible: (i = 0) => ({
    opacity: 1,
    x: 0,
    transition: {
      duration: animationTimings.medium,
      delay: animationDelays.short + (i * staggerIntervals.tight),
      ease: easings.easeOut
    }
  })
};

/**
 * CTA button animation variants
 */
export const ctaButtonVariants: Variants = {
  initial: { 
    x: 0,
    willChange: 'transform'
  },
  hover: {
    x: 5,
    transition: {
      duration: animationTimings.fast,
      ease: easings.premium.easeOutQuint,
      repeat: 0
    }
  }
};

/**
 * Section title animation variants
 */
export const sectionTitleVariants: Variants = {
  hidden: { 
    opacity: 0, 
    y: 20,
    willChange: 'transform, opacity'
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: animationTimings.medium,
      ease: easings.premium.easeOutQuint
    }
  }
};

/**
 * Section description animation variants
 */
export const sectionDescriptionVariants: Variants = {
  hidden: { 
    opacity: 0, 
    y: 20,
    willChange: 'transform, opacity'
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: animationTimings.medium,
      delay: animationDelays.short,
      ease: easings.premium.easeOutQuint
    }
  }
};